// dependencies
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useWindowSize } from "usehooks-ts";

import { faGripDotsVertical } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import uuid from "../../../utils/GenerateUUID";

// styles
import css from "./GridToolPanel.module.scss";

export interface MenuOption {
	id: string;
	label: string;
	icon: ReactNode;
	content: ReactNode;
}

interface PanelControllerProps {
	menuOptions: MenuOption[];
	gridRef: React.RefObject<any>;
	parentRef: React.RefObject<any>;
	panelInitialWidth: number;
	initialOpenPanel: string | null;
}

interface PanelProps {
	children: ReactNode;
	gridRef: React.RefObject<any>;
	parentRef: React.RefObject<any>;
	initialWidth: number;
	closePanel: () => void;
}

/**
 * Renders the panel. This is the panel that is resizable. When the user drags
 * the panel, the grid is resized to fit the panel. Note that the panel width
 * is not stored in state, this is an optimization to avoid re-rendering the
 * grid when the panel is resized.
 */
const Panel = (props: PanelProps) => {
	const { initialWidth, gridRef, parentRef, children, closePanel } = props;

	const { width: appWidth } = useWindowSize();

	// STATE
	const [isDragging, setIsDragging] = useState<boolean>(false);

	// The ref to the panel is used for resizing.
	const panelRef = useRef<HTMLDivElement | null>(null);

	/**
	 * This event is attached to resizer element. When the user clicks on the
	 * resizer, we enter dragging mode which will allow the user to drag to
	 * resize the panel.
	 */
	const handleMouseDown = () => setIsDragging(true);

	/**
	 * Given the panel width, this function will update the width of the grid
	 * and the width of the panel as a function fo the parent width.
	 */
	const updateWidths = (panelWidth: number, isClosing?: boolean) => {
		// if we are in commitHookEffectListUnmount there may not be a parentRef.current
		if (parentRef.current) {
			const parentWidth = parentRef.current.getBoundingClientRect().width;

			let clampedPanelWidth = 0;

			// if we are not closing the panel, we want to clamp the width of panel
			// to be between 40px and parent width - 10px. This is to ensure the panel
			// is not too small or too large and can also be 'grabbed' by the user.
			if (!isClosing) {
				clampedPanelWidth = Math.max(40, Math.min(parentWidth - 10, panelWidth));
				if (panelRef.current) {
					panelRef.current.style.width = `${clampedPanelWidth}px`;
					if (parseInt(panelRef.current.style.width, 10) < 215) {
						closePanel();
					}
				}
			}

			gridRef.current.style.width = `${parentWidth - clampedPanelWidth}px`;
		}
	};

	/**
	 * Given the cursor's position, this will calculate the new width of the
	 * panel and call the updateWidths function to update the grid and panel
	 * widths.
	 */
	const onDrag = (clientX: number) => {
		const panel = panelRef.current;

		if (!panel) {
			return;
		}

		const { right } = panel.getBoundingClientRect();
		const width = right - clientX;

		updateWidths(width);
	};

	/**
	 * ON MOUNT.
	 * When the panel is first mounted, the grid is resized to fit the panel. We
	 * then add event listeners that will listen for the 'mouse up' event. Whenever
	 * there is a 'mouse up' event, the user has finished dragging so we update the
	 * dragging state to be false.
	 */
	useEffect(() => {
		// updates the grid and panel dimensions
		updateWidths(initialWidth);

		const handleMouseUp = () => setIsDragging(false);
		document.addEventListener("mouseup", handleMouseUp);

		// cleanup: remove event listeners, update widths, inform parent of
		// event
		return () => {
			document.removeEventListener("mouseup", handleMouseUp);
			updateWidths(0, true);
		};
	}, [appWidth]); // app width used as dependency to resize grid if screen is resized (to prevent panel from detatching from grid)

	/**
	 * DRAG STATE CHANGE.
	 * Whenever the drag state changes, we check to see if it is true. If it
	 * is, we add an event listener to listen for the 'mouse move' event.
	 * Whenever there is a 'mouse move' event, we call the onDrag function with
	 * the clientX position.
	 */
	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			onDrag(e.clientX);
		};

		if (isDragging) {
			window.addEventListener("mousemove", handleMouseMove);
		}

		// cleanup: remove event listeners
		return () => window.removeEventListener("mousemove", handleMouseMove);
	}, [isDragging, onDrag]);

	return (
		<div ref={panelRef} className={`${css.panel} ${isDragging ? css.isDragging : ""}`}>
			<div className={css.panelContent}>{children}</div>

			<div
				className={`${css.horizontalResizer} ${isDragging ? css.isDragging : ""}`}
				onMouseDown={handleMouseDown}
			>
				<div className={css.border} />
				<div className={css.grip}>
					<FontAwesomeIcon icon={faGripDotsVertical} />
				</div>
			</div>
		</div>
	);
};

/**
 * Renders the column to the side of the grid and the tool panel. The column
 * provides buttons that toggle which panel is visible.
 */
const GridToolPanelController = (props: PanelControllerProps) => {
	const { menuOptions, gridRef, parentRef, panelInitialWidth, initialOpenPanel } = props;
	// STATE
	const [openPanel, setOpenPanel] = useState<null | string>(initialOpenPanel);

	// The ref to the grid is used to set the last width of the panel. This
	// allows us to 'remember'
	const lastWidthUsed = useRef(panelInitialWidth);

	// reset ag grid to 100% if panel is closed
	useEffect(() => {
		if (!openPanel) {
			gridRef.current.style.width = "100%";
		}
	}, [openPanel]);

	/**
	 * Get the properties of the panel that is currently open. This
	 * is used to render the panel content. Since the menuOptions is stored as
	 * an array of objects, we use the useMemo hook to avoid unnecessary find
	 * operations.
	 */
	const activePanel = useMemo(() => {
		if (!openPanel) {
			return null;
		}

		return menuOptions.find((option) => option.id === openPanel);
	}, [menuOptions, openPanel]);

	/**
	 * Clicking on a button will toggle the panel. A 'null' value will
	 * close the panel.
	 */
	const handleMenuItemClick = (panelName) => {
		const nextPanel = openPanel === panelName ? null : panelName;
		setOpenPanel(nextPanel);
	};

	return menuOptions.length ? (
		<div className={css.main} data-testid="ToolPanel">
			<div className={css.menu}>
				{menuOptions.map((option) => {
					const isActive = openPanel === option.id;
					return (
						<button
							key={`${option.id}-${uuid()}`}
							className={`${css.menuOption} ${isActive ? css.isActive : ""}`}
							onClick={() => handleMenuItemClick(option.id)}
							data-testid={`ToolPanelOption-${option.id}`}
						>
							<div className={css.menuOptionContent}>
								<div className={css.menuOptionIcon}>{option.icon}</div>
								<div className={css.menuOptionLabel}>{option.label}</div>
							</div>

							{isActive ? <div className={css.menuOptionUnderline} /> : null}
						</button>
					);
				})}
			</div>

			{activePanel ? (
				<Panel
					initialWidth={lastWidthUsed.current}
					gridRef={gridRef}
					parentRef={parentRef}
					closePanel={() => setOpenPanel(null)}
				>
					{activePanel.content}
				</Panel>
			) : null}
		</div>
	) : null;
};

export default GridToolPanelController;
